import { Head, Link, router } from "@inertiajs/react"
import ProductionHeader from "@/components/admin/production/ProductionHeader"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { SharedProductionProps } from "@/components/admin/production/types"

interface AssetSummary {
  id: number
  label: string
  asset_kind: string | null
  purchase_cost_cents: number
  purchased_on: string | null
  retired_at: string | null
  total_units_produced: number
  cost_per_unit_cents: number
}

interface IndexProps extends SharedProductionProps {
  assets: AssetSummary[]
}

function formatEuros(cents: number): string {
  return (cents / 100).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  })
}

export default function AssetsIndex({ store, paths, assets }: IndexProps) {
  return (
    <>
      <Head title="Équipements · Production" />
      <ProductionHeader
        title="Équipements amortis"
        subtitle="Écrans, cadres et outils réutilisables. Leur coût est réparti automatiquement sur toutes les unités produites."
        storeName={store.name ?? undefined}
        paths={paths}
        actions={
          <Link href={`${paths.assets_index}/new`}>
            <Button size="sm">+ Nouvel équipement</Button>
          </Link>
        }
      />
      <main className="mx-auto max-w-6xl px-6 py-8">
        {assets.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
            <p className="text-sm text-gray-500">
              Aucun équipement enregistré.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Coût d&rsquo;achat</TableHead>
                <TableHead>Unités produites</TableHead>
                <TableHead>Coût/unité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.label}</TableCell>
                  <TableCell>{a.asset_kind ?? "—"}</TableCell>
                  <TableCell>{formatEuros(a.purchase_cost_cents)}</TableCell>
                  <TableCell>{a.total_units_produced}</TableCell>
                  <TableCell>
                    {a.total_units_produced === 0
                      ? "—"
                      : formatEuros(a.cost_per_unit_cents)}
                  </TableCell>
                  <TableCell>
                    {a.retired_at ? (
                      <span className="text-xs uppercase text-gray-400">
                        Retiré
                      </span>
                    ) : (
                      <span className="text-xs uppercase text-green-700">
                        Actif
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Link href={`${paths.assets_index}/${a.id}/edit`}>
                      <Button variant="outline" size="sm">
                        Éditer
                      </Button>
                    </Link>
                    {!a.retired_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (window.confirm("Retirer cet équipement ?")) {
                            router.patch(
                              `${paths.assets_index}/${a.id}/retire`
                            )
                          }
                        }}
                      >
                        Retirer
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </main>
    </>
  )
}
